import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Check, LoaderCircle } from "lucide-react";
import type { Project, ProjectMember, User } from "@lumen/shared";
import { useModelCatalog } from "../../ModelCatalogContext";
import { api, ApiError } from "../../api";
import { t } from "../../i18n";
import type {
  ComposerActivity,
  ComposerOptions,
} from "../../components/composer/Composer";
import { supportsActivity } from "../chat/ChatPage.utils";
import { ComposerDefaultsPanel } from "../../components/projects/ComposerDefaultsPanel";
import { PROJECTS_CHANGED_EVENT } from "../../components/projects/projectEvents";

const activities: ComposerActivity[] = [
  "ask",
  "image",
  "audio",
  "video",
  "speech",
  "transcription",
  "model3d",
  "sprite",
  "tileset",
];
function removeModels(
  value: Partial<ComposerOptions>,
): Partial<ComposerOptions> {
  const settings = value.generationParameters?.activitySettings;
  return {
    ...value,
    generationParameters: {
      ...(value.generationParameters ?? {}),
      activitySettings: settings
        ? Object.fromEntries(
            Object.entries(settings).map(([activity, current]) => [
              activity,
              current && typeof current === "object"
                ? Object.fromEntries(
                    Object.entries(current).filter(
                      ([key]) => key !== "modelId",
                    ),
                  )
                : current,
            ]),
          )
        : settings,
    },
  };
}

export function ProjectEditorPage({ user }: { user: User }) {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const [savedProjectId, setSavedProjectId] = useState<string | undefined>(
    projectId,
  );
  const activeProjectId = projectId ?? savedProjectId;
  const editing = Boolean(activeProjectId);
  const canShare = user.organizationPermissions.includes("projects:manage");
  const [members, setMembers] = useState<ProjectMember[]>([]);
  const [memberUserId, setMemberUserId] = useState("");
  const { models, loadModels } = useModelCatalog();
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [visibility, setVisibility] = useState<"private" | "organization">(
    "private",
  );
  const [defaultActivity, setDefaultActivity] =
    useState<ComposerActivity>("ask");
  const [composerDefaults, setComposerDefaults] = useState<
    Partial<ComposerOptions>
  >({});
  const [loading, setLoading] = useState(editing);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    setSavedProjectId(projectId);
    setLoading(Boolean(projectId));
    setError("");
    if (projectId) {
      setName("");
      setDescription("");
      setVisibility("private");
      setDefaultActivity("ask");
      setComposerDefaults({});
    }
    let active = true;
    void loadModels().catch(() => undefined);
    if (!projectId) {
      setLoading(false);
      return () => {
        active = false;
      };
    }
    void api<Project>(`/projects/${projectId}`)
      .then((project) => {
        if (!active) return;
        setName(project.name);
        setDescription(project.description ?? "");
        setVisibility(project.visibility);
        setDefaultActivity(
          (project.defaultActivity as ComposerActivity) ?? "ask",
        );
        setComposerDefaults(
          project.composerDefaults as Partial<ComposerOptions>,
        );
        setMembers(project.members ?? []);
      })
      .catch((reason) => {
        if (!active) return;
        if (reason instanceof ApiError && reason.status === 404) {
          navigate("/projects", { replace: true });
          return;
        }
        setError(
          reason instanceof Error
            ? reason.message
            : t("Project could not be loaded."),
        );
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [loadModels, navigate, projectId]);

  const availableActivities = useMemo(
    () =>
      activities.filter(
        (activity) =>
          activity === "ask" ||
          models.some((model) => supportsActivity(model, activity)),
      ),
    [models],
  );
  const save = async () => {
    if (!name.trim()) return;
    setBusy(true);
    setError("");
    const projectDefaults = removeModels({
      ...composerDefaults,
      generationParameters: {
        ...(composerDefaults.generationParameters ?? {}),
        activity: defaultActivity,
      },
    });
    try {
      const project = await api<Project>(
        activeProjectId ? `/projects/${activeProjectId}` : "/projects",
        {
          method: activeProjectId ? "PATCH" : "POST",
          body: JSON.stringify({
            name: name.trim(),
            description: description.trim() || null,
            visibility,
            defaultActivity,
            composerDefaults: projectDefaults,
          }),
        },
      );
      setSavedProjectId(project.id);
      if (!activeProjectId) {
        // Like the Chat view, a project's first conversation only exists once
        // a message is sent. Land on the project's draft composer instead of
        // creating an empty conversation.
        window.dispatchEvent(new Event(PROJECTS_CHANGED_EVENT));
        navigate(`/projects/conversations/new?project=${project.id}`, {
          replace: true,
        });
      } else {
        window.dispatchEvent(
          new CustomEvent(PROJECTS_CHANGED_EVENT, { detail: project }),
        );
      }
    } catch (reason) {
      setError(
        reason instanceof Error
          ? reason.message
          : t("Project could not be saved."),
      );
    } finally {
      setBusy(false);
    }
  };

  const canManageMembers =
    Boolean(activeProjectId) &&
    (user.organizationPermissions.includes("projects:manage") ||
      members.some(
        (member) => member.userId === user.id && member.role === "owner",
      ));
  const addMember = async () => {
    if (!activeProjectId || !memberUserId.trim()) return;
    try {
      const member = await api<ProjectMember>(
        `/projects/${activeProjectId}/members`,
        {
          method: "POST",
          body: JSON.stringify({ userId: memberUserId.trim() }),
        },
      );
      setMembers((current) => [
        ...current.filter((item) => item.userId !== member.userId),
        member,
      ]);
      setMemberUserId("");
    } catch (reason) {
      setError(
        reason instanceof Error
          ? reason.message
          : t("Member could not be added."),
      );
    }
  };
  const removeMember = async (member: ProjectMember) => {
    if (!activeProjectId) return;
    try {
      await api(`/projects/${activeProjectId}/members/${member.userId}`, {
        method: "DELETE",
      });
      setMembers((current) =>
        current.map((item) =>
          item.userId === member.userId ? { ...item, state: "removed" } : item,
        ),
      );
    } catch (reason) {
      setError(
        reason instanceof Error
          ? reason.message
          : t("Member could not be removed."),
      );
    }
  };

  if (loading)
    return (
      <div className="grid flex-1 place-items-center">
        <LoaderCircle className="animate-spin text-lumen-600" />
      </div>
    );
  return (
    <div className="min-h-0 flex-1 overflow-y-auto p-4 sm:p-8">
      <div className="mx-auto max-w-4xl">
        <Link to="/projects" className="button mb-6">
          <ArrowLeft size={16} /> {t("Back to projects")}
        </Link>
        <div className="panel p-5 sm:p-7">
          <div className="flex items-start justify-between gap-4">
            <div>
              <p className="text-xs font-bold tracking-[.18em] text-lumen-600 uppercase">
                {t("Project")}
              </p>
              <h1 className="mt-1 text-2xl font-bold">
                {editing ? t("Edit project") : t("Create new project")}
              </h1>
              <p className="mt-2 text-sm text-stone-500">
                {t(
                  "Set the defaults used when a new conversation is created in this project.",
                )}
              </p>
            </div>
          </div>
          <div className="mt-6 grid gap-4 sm:grid-cols-2">
            <label className="block">
              <span className="label">{t("Project name")}</span>
              <input
                className="field"
                value={name}
                onChange={(event) => setName(event.target.value)}
                maxLength={120}
              />
            </label>
            <label className="block">
              <span className="label">{t("Default mode")}</span>
              <select
                className="field"
                value={defaultActivity}
                onChange={(event) =>
                  setDefaultActivity(event.target.value as ComposerActivity)
                }
              >
                {availableActivities.map((activity) => (
                  <option key={activity} value={activity}>
                    {activity === "ask" ? t("Ask") : activity}
                  </option>
                ))}
              </select>
            </label>
          </div>
          <label className="mt-4 block">
            <span className="label">{t("Description")}</span>
            <textarea
              className="field min-h-24"
              value={description}
              onChange={(event) => setDescription(event.target.value)}
              maxLength={2000}
            />
          </label>
          <fieldset className="mt-5 rounded-xl border border-stone-200 p-4 dark:border-white/10">
            <legend className="px-1 text-sm font-semibold">
              {t("Visibility")}
            </legend>
            <div className="flex flex-wrap gap-4 text-sm">
              <label className="flex items-center gap-2">
                <input
                  type="radio"
                  checked={visibility === "private"}
                  onChange={() => setVisibility("private")}
                />
                {t("Private")}
              </label>
              <label
                className={`flex items-center gap-2 ${canShare ? "" : "text-stone-400"}`}
              >
                <input
                  type="radio"
                  disabled={!canShare}
                  checked={visibility === "organization"}
                  onChange={() => setVisibility("organization")}
                />
                {t("Organization")}
              </label>
            </div>
            {!canShare && (
              <p className="mt-2 text-xs text-stone-400">
                {t(
                  "You need organization project permission to share a project.",
                )}
              </p>
            )}
          </fieldset>
          {editing && (
            <section className="mt-6 rounded-xl border border-stone-200 p-4 dark:border-white/10">
              <h2 className="text-lg font-bold">{t("Project members")}</h2>
              <div className="mt-3 space-y-2">
                {members
                  .filter((member) => member.state !== "removed")
                  .map((member) => (
                    <div
                      key={member.userId}
                      className="flex items-center justify-between gap-3 text-sm"
                    >
                      <span className="min-w-0 truncate">
                        {member.name}{" "}
                        <span className="text-stone-400">({member.email})</span>
                        {member.state === "stale" && (
                          <span className="ml-2 text-amber-600">
                            {t("Stale")}
                          </span>
                        )}
                      </span>
                      {canManageMembers && member.role !== "owner" && (
                        <button
                          type="button"
                          className="button text-red-600"
                          onClick={() => void removeMember(member)}
                        >
                          {t("Remove")}
                        </button>
                      )}
                    </div>
                  ))}
              </div>
              {canManageMembers && (
                <div className="mt-4 flex gap-2">
                  <input
                    className="field min-w-0 flex-1"
                    placeholder={t("User ID")}
                    value={memberUserId}
                    onChange={(event) => setMemberUserId(event.target.value)}
                  />
                  <button
                    type="button"
                    className="button-primary"
                    onClick={() => void addMember()}
                  >
                    {t("Add")}
                  </button>
                </div>
              )}
            </section>
          )}
          <section className="mt-6">
            <h2 className="text-lg font-bold">{t("Composer defaults")}</h2>
            <p className="mt-1 text-sm text-stone-500">
              {t(
                "Configure each mode independently. The primary model remains a conversation-level choice.",
              )}
            </p>
            <ComposerDefaultsPanel
              value={composerDefaults}
              models={models}
              availableActivities={availableActivities}
              organizationDefaultsOnly={visibility === "organization"}
              onChange={setComposerDefaults}
            />
          </section>
          {error && (
            <p className="mt-4 text-sm text-red-600 dark:text-red-300">
              {error}
            </p>
          )}
          <div className="mt-6 flex justify-end">
            <button
              className="button-primary"
              disabled={busy || !name.trim()}
              onClick={() => void save()}
            >
              {busy ? (
                <LoaderCircle className="animate-spin" size={16} />
              ) : (
                <Check size={16} />
              )}{" "}
              {t("Save project")}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
